import { Resources } from 'constants/resources';

import { useEffect, useState } from 'react';

import { editLogo } from 'config/requests/methodLogos';
import { useGoBack } from 'hooks/useGoBack';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { MethodLogosDeleteDialog } from 'pages/MethodLogos/components/MethodLogosDeleteDialog/MethodLogosDeleteDialog';
import { queryClient } from 'providers/queryClient';
import { useNotify, useEditController } from 'react-admin';
import { useForm, FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { DeleteButton, SaveButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { EditPage } from 'shared/react-admin/Pages/EditPage/EditPage';
import { MethodLogo } from 'types/embedded/methodLogo';
import { Permission } from 'types/embedded/permission';
import { getId } from 'utils';

import { MethodLogosEditForm } from './MethodLogosEditForm';
import { MethodLogosEditFormSkeleton } from './MethodLogosEditFormSkeleton';

type MethodLogosEditFormValues = {
  file?: FileList | string;
  filePath: string;
  name: string;
};

export const MethodLogosEdit: React.FC = () => {
  const notify = useNotify();
  const { goBack } = useGoBack();
  const { hasResourcePermissions } = usePermissions();

  const [errors, setErrors] = useState<any>();
  const [isShowImage, setIsShowImage] = useState(false);
  const form = useForm<MethodLogosEditFormValues>({
    defaultValues: {
      name: '',
      filePath: '',
      file: '',
    },
  });

  const { id: currentPath } = useParams();
  const id = getId(currentPath);
  const { record } = useEditController();

  const { handleCloseRecordModal, openRecordModal, handleOpenRecordModal } =
    useRecordModal<MethodLogo>();

  useEffect(() => {
    if (record?.name) {
      form.reset({ name: record.name, filePath: record.filePath, file: '' });
    }
  }, [record]);

  const submitForm = (data: MethodLogosEditFormValues) => {
    const formData = new FormData();
    if (data?.file?.[0]) {
      formData.append('file', data.file[0]);
    }
    formData.append('filePath', data.filePath);
    formData.append('name', data.name);

    editLogo(id?.toString(), formData)
      .then(() => {
        notify('Логотип изменен!', { type: 'info' });
        goBack();
        queryClient.invalidateQueries({ queryKey: [Resources.MethodLogos] });
      })
      .catch((e) => {
        setErrors(e?.data);
      });
  };

  return (
    <>
      <EditPage listName="Логотипы методов" title="Редактирование логотипа">
        {!record ? (
          <MethodLogosEditFormSkeleton />
        ) : (
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(submitForm)}>
              <MethodLogosEditForm errors={errors} setIsShowImage={setIsShowImage} />
              <CardActionsButtons
                rightActionsSlot={[
                  hasResourcePermissions(Resources.MethodLogos, [
                    Permission.Full,
                    Permission.Delete,
                  ]) && (
                    <DeleteButton
                      key="delete-button"
                      label="Удалить"
                      onClick={() => handleOpenRecordModal()}
                      variant="outlined"
                    />
                  ),
                  <SaveButton disabled={!isShowImage} key="method-logos-edit" label="Сохранить" />,
                ]}
                sx={{ marginTop: 'auto', padding: 0 }}
              />
            </form>
          </FormProvider>
        )}
      </EditPage>
      {openRecordModal && (
        <MethodLogosDeleteDialog
          onClose={handleCloseRecordModal}
          open={openRecordModal}
          record={record}
        />
      )}
    </>
  );
};
